import React from 'react'
import {
  ClinicCounterBodyDoctorDetails,
  MentorAvatarImg,
  MentorAvatarPlaceholder,
  MentorAvatarWrap,
  PresenceBadge,
} from '../../ClinicCounterStyles'
import { en, local } from '../../oohTranslations'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

function getInitials(mentor) {
  if (!mentor) return '?'
  const first = mentor?.firstName || mentor?.name || ''
  const last = mentor?.lastName || ''
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase()
  if (first) return first.slice(0, 2).toUpperCase()
  return '?'
}

function PresencePill({ present, language }) {
  const key = present ? 'present' : 'absent'
  const enLabel = en(key)
  const localLabel = local(key, language)
  return (
    <PresenceBadge present={present}>
      <span
        style={{
          display: 'inline-block',
          width: 'clamp(7px, 0.7vw, 10px)',
          height: 'clamp(7px, 0.7vw, 10px)',
          borderRadius: '999px',
          background: '#fff',
          boxShadow: '0 0 0 2px rgba(255,255,255,0.35)',
          marginRight: '8px',
          verticalAlign: 'middle',
        }}
      />
      <span style={{ verticalAlign: 'middle' }}>{enLabel}</span>
      {localLabel && (
        <span
          style={{ verticalAlign: 'middle', marginLeft: '6px', opacity: 0.92, fontSize: '0.85em' }}
        >
          · {localLabel}
        </span>
      )}
    </PresenceBadge>
  )
}

export default function OneMentorVideoAndOtherMentorCard({
  loading,
  mentor,
  mentorTitle = 'Doctor',
  checkedIn = false,
  hideMentorDetails = false,
  isRightColumn = false,
  displayLanguage,
  expanded = false,
  children,
}) {
  const displayTitle = mentorTitle || (() => {
    if (mentor?.categories && Array.isArray(mentor.categories)) {
      if (mentor.categories.includes('therapist')) return 'Therapist'
      if (mentor.categories.includes('doctor')) return 'Doctor'
    }
    return 'Practitioner'
  })()

  const hasProfilePic = !hideMentorDetails && mentor?.profilePicId
  const fullName =
    mentor?.name || [mentor?.firstName, mentor?.lastName].filter(Boolean).join(' ') || '—'

  // "Hero" treatment (oversized avatar + name) only when the card is
  // identity-only. When the card also carries an in-card token block
  // (two-mentor symmetric mode), keep the avatar modest so tokens fit.
  const hasChildren = Boolean(children)
  const bigHero = expanded && !hasChildren

  return (
    <ClinicCounterBodyDoctorDetails $isRightColumn={isRightColumn}>
      {loading && !mentor ? (
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(14px, 1.2vw, 18px)' }}>
          Loading…
        </div>
      ) : (
        <>
          {!hideMentorDetails && (
            <MentorAvatarWrap
              style={
                bigHero
                  ? { maxWidth: 'min(360px, 50vh)' }
                  : expanded
                    ? { maxWidth: 'min(200px, 26vh)' }
                    : undefined
              }
            >
              {hasProfilePic ? (
                <MentorAvatarImg
                  src={getImageLink(mentor?.profilePicId)}
                  alt={mentor?.firstName || mentor?.name || 'Practitioner'}
                />
              ) : (
                <MentorAvatarPlaceholder>{getInitials(mentor)}</MentorAvatarPlaceholder>
              )}
            </MentorAvatarWrap>
          )}
          {/* Presence chip rendered as a SIBLING below the avatar, never
              overlapping the practitioner's face. */}
          <PresencePill present={checkedIn} language={displayLanguage} />
          <div
            className="d-flex flex-column"
            style={{
              textAlign: 'center',
              width: '100%',
              color: 'rgba(255,255,255,0.96)',
              gap: 0,
            }}
          >
            {!hideMentorDetails && (
              <div id="screening-mentor-name">
                <div
                  style={{
                    fontSize: bigHero
                      ? 'clamp(24px, 2.2vw, 40px)'
                      : 'clamp(17px, 1.55vw, 26px)',
                    fontWeight: 700,
                    lineHeight: 1.22,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {fullName}
                </div>
                <div
                  style={{
                    marginTop: bigHero ? '12px' : '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: bigHero
                      ? 'clamp(5px, 0.6vw, 8px) clamp(14px, 1.4vw, 20px)'
                      : 'clamp(3px, 0.4vw, 5px) clamp(10px, 1.1vw, 14px)',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontSize: bigHero
                      ? 'clamp(13px, 1.1vw, 18px)'
                      : 'clamp(10px, 0.85vw, 13px)',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.78)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {displayTitle}
                </div>
              </div>
            )}
            {children && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  marginTop: hideMentorDetails ? 0 : '14px',
                  paddingTop: hideMentorDetails ? 0 : '14px',
                  borderTop: hideMentorDetails ? 'none' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {children}
              </div>
            )}
          </div>
        </>
      )}
    </ClinicCounterBodyDoctorDetails>
  )
}
